import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Selection for the credits picker: which BOM lines go to supplier discovery
 * when the org can only afford some of them. At most `cap` lines can be
 * ticked. Nothing is pre-selected — which lines matter first is the buyer's
 * call, not an order Robbie should guess.
 *
 * `lockedIds` are lines that cost nothing more (already paid for): they are
 * always kept, cannot be unticked, and sit outside the cap.
 */

/**
 * The fields the picker reads off a BOM line. Local on purpose: the BOM line
 * types are being reshaped on another branch; merge onto `Component` from
 * `types/bom.types.ts` once that lands.
 */
export interface PickerComponent {
  componentId?: string;
  name?: string;
  partNumber?: string;
  mpn?: string;
  quantity?: number | string;
}

const NONE: string[] = [];

export const useCreditsPicker = (
  components: PickerComponent[],
  cap: number,
  open: boolean,
  lockedIds: string[] = NONE
) => {
  const [selected, setSelected] = useState<string[]>([]);

  // Every opening starts from an empty pick.
  useEffect(() => {
    if (open) setSelected([]);
  }, [open]);

  // A second refusal can lower the cap (the balance moved): keep the lines
  // picked first, drop the rest.
  useEffect(() => {
    setSelected((current) => (current.length > cap ? current.slice(0, Math.max(cap, 0)) : current));
  }, [cap]);

  // Only lines with an id can be sent; the BOM upload gives every line one.
  const rows = useMemo(() => components.filter((c): c is PickerComponent & { componentId: string } =>
    typeof c.componentId === 'string' && c.componentId.length > 0), [components]);

  const locked = useMemo(() => {
    const wanted = new Set(lockedIds);
    return new Set(rows.filter((row) => wanted.has(row.componentId)).map((row) => row.componentId));
  }, [rows, lockedIds]);

  const toggle = useCallback(
    (componentId: string) =>
      setSelected((current) => {
        if (locked.has(componentId)) return current;
        if (current.includes(componentId)) return current.filter((id) => id !== componentId);
        return current.length < cap ? [...current, componentId] : current;
      }),
    [cap, locked]
  );

  /** Everything that goes forward: the locked lines plus the ticked ones, in BOM order. */
  const kept = useMemo(
    () => rows.filter((row) => locked.has(row.componentId) || selected.includes(row.componentId)).map((row) => row.componentId),
    [rows, locked, selected]
  );

  return { rows, selected, locked, kept, toggle, atCap: selected.length >= cap };
};

export default useCreditsPicker;
